import { useState, useContext } from "react";
import Link from "next/link";
import Image from "next/image";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { truncateText } from "utils/text";
import { BsClock, BsCheck2 } from "react-icons/bs";
import LoadingAnimation from "@/components/ui/loaders/LoadingAnimation/LoadingAnimation";
import FavoriteButton from "components/ui/buttons/FavoriteButton/FavoriteButton";
import styles from "./ViatorProductCard.module.scss";
import {
  formatViatorDuration,
  generateViatorProductStars,
} from "utils/helpers/viator";
import Button from "@/components/ui/buttons/Button/Button";
import { CurrencyContext } from "contexts/currency";

export default function ViatorProductCard({
  cardType = "full",
  product,
  isLoaded,
  showDescription = true,
  truncateTitleTo = 52,
  truncateDescriptionTo = 180,
}) {
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const { currency } = useContext(CurrencyContext);

  // Handle loading state

  if (!isLoaded) {
    return <LoadingSkeleton cardType={cardType} />;
  }

  if ((isLoaded && !product) || !product?.productCode || !product?.productUrl) {
    return null;
  }

  // Destructure basic product data
  const {
    title,
    description,
    pricing,
    pricingInfo,
    reviews,
    duration,
    destination,
  } = product;

  // Choose largest image
  const images = product?.images
    ? product.images[0].variants.sort((a, b) => b.width - a.width)
    : [];

  const image = images ? images[0] : { url: "", width: 0, height: 0 };

  // Format location for readability
  let formattedLocation = "";

  if (!!destination) {
    formattedLocation = [
      destination?.city?.destination_name ?? null,
      destination?.region?.destination_name ?? null,
    ]
      .filter((item) => item)
      .join(", ");
  } else if (!!product?.destinations) {
    const destinationGeocodeContext =
      product?.destinations?.[0]?.geocodeInfo?.[0]?.context;

    const place = destinationGeocodeContext?.find(
      (contextItem) => contextItem?.id?.split(".")[0] === "place"
    )?.text;

    const region = destinationGeocodeContext?.find(
      (contextItem) => contextItem?.id?.split(".")[0] === "region"
    )?.text;

    const country = destinationGeocodeContext?.find(
      (contextItem) => contextItem?.id?.split(".")[0] === "country"
    )?.text;

    formattedLocation = `${place ? place + ", " : ""}${
      region ? region + ", " : ""
    }${country ? country : ""}`;
  }

  // Format activity duration by converting to largest available unit
  let formattedActivityDuration =
    formatViatorDuration({
      fixedDurationInMinutes: duration?.fixedDurationInMinutes,
      variableDurationData: {
        fromMinutes: duration?.variableDurationFromMinutes,
        toMinutes: duration?.variableDurationToMinutes,
      },
    }) ?? null;

  // Truncate description
  const truncatedDescription = truncateText(description, truncateDescriptionTo);

  // Check for free cancellation
  const freeCancellation = product?.flags
    ? product.flags.find((flag) => flag === "FREE_CANCELLATION")
    : null;

  let pricingString = "";

  // Format pricing
  if (!!pricing) {
    pricingString = !!pricing
      ? `$${Math.round(parseFloat(pricing.summary.fromPrice))} ${
          pricing.currency
        }`
      : "";
  } else if (!!pricingInfo) {
    const currencies = product?.availability?.currencies ?? null;
    if (currencies) {
      const currentCurrency = currencies[currency.code.toUpperCase()];
      const currentCurrencyString = currentCurrency.toFixed(0);
      pricingString = `${currency.symbol}${currentCurrencyString}`;
    }
  }

  const pricingElement = !!pricingString ? (
    <>
      <span className={`${styles.pricingFromLabel}`}>from</span>{" "}
      <span className={`${styles.fromPrice} `}>{pricingString}</span>
    </>
  ) : null;

  // Image element
  const imageElement = (
    <div className={`${styles.imageWrapper}`}>
      {!isImageLoaded && (
        <div className={`${styles.loadingAnimation}`}>
          <LoadingAnimation />
        </div>
      )}
      {!!image?.url && (
        <Image
          src={image.url}
          alt={product.images[0].caption}
          width={image.width}
          height={image.height}
          onLoad={() => setIsImageLoaded(true)}
          className={`${styles.image}`}
        />
      )}

      <FavoriteButton
        objectId={product?.productCode}
        favoriteType="activities"
        classNames={`${styles.favoriteButton}`}
      />
    </div>
  );

  if (isLoaded && cardType === "mini") {
    return (
      <ViatorProductCardMini
        product={product}
        image={image}
        isImageLoaded={isImageLoaded}
        setIsImageLoaded={setIsImageLoaded}
        formattedLocation={formattedLocation}
        formattedActivityDuration={formattedActivityDuration}
        truncatedDescription={truncatedDescription}
        freeCancellation={freeCancellation}
        pricingElement={pricingElement}
        imageElement={imageElement}
        isLoaded={isLoaded}
        showDescription={showDescription}
        truncateTitleTo={truncateTitleTo}
        truncateDescriptionTo={truncateDescriptionTo}
      />
    );
  }

  if (isLoaded && cardType === "large") {
    return (
      <ViatorProductCardLarge
        product={product}
        image={image}
        isImageLoaded={isImageLoaded}
        setIsImageLoaded={setIsImageLoaded}
        formattedLocation={formattedLocation}
        formattedActivityDuration={formattedActivityDuration}
        truncatedDescription={truncatedDescription}
        freeCancellation={freeCancellation}
        pricingElement={pricingElement}
        imageElement={imageElement}
        isLoaded={isLoaded}
        showDescription={showDescription}
        truncateTitleTo={truncateTitleTo}
        truncateDescriptionTo={truncateDescriptionTo}
      />
    );
  }

  if (isLoaded) {
    return (
      <ViatorProductCardFull
        product={product}
        image={image}
        isImageLoaded={isImageLoaded}
        setIsImageLoaded={setIsImageLoaded}
        formattedLocation={formattedLocation}
        formattedActivityDuration={formattedActivityDuration}
        truncatedDescription={truncatedDescription}
        freeCancellation={freeCancellation}
        pricingElement={pricingElement}
        imageElement={imageElement}
        isLoaded={isLoaded}
        showDescription={showDescription}
        truncateTitleTo={truncateTitleTo}
        truncateDescriptionTo={truncateDescriptionTo}
      />
    );
  }
}

export function ViatorProductCardFull({
  product,
  formattedLocation,
  formattedActivityDuration,
  truncatedDescription,
  freeCancellation,
  pricingElement,
  imageElement,
  showDescription = true,
  truncateTitleTo = 64,
}) {
  const { title, reviews } = product;

  return (
    <article
      className={`${styles.container} ${styles.full} wn-viator-product-card`}
      id={`viator-product-card-${product?.productCode}`}
    >
      <Link
        href={product.productUrl}
        className={`${styles.link}`}
        target="_blank"
      >
        {imageElement}
        <div className={`${styles.content}`}>
          <div className={`${styles.topRow}`}>
            <Title
              title={title}
              isMobile={false}
              formattedLocation={formattedLocation}
              truncateTitleTo={truncateTitleTo}
            />
            <Title
              title={title}
              isMobile={true}
              formattedLocation={formattedLocation}
            />

            <div className={`${styles.price} ${styles.hideOnMobile}`}>
              {pricingElement}
            </div>
          </div>
          <div className={`${styles.rating}`}>
            <StarRating reviews={reviews} productCode={product?.productCode} />
          </div>
          <Flags freeCancellation={freeCancellation} isMobile={true} />
          <div className={`${styles.price} ${styles.hideOnDesktop}`}>
            {pricingElement}
          </div>
          {showDescription && (
            <p className={`${styles.description} ${styles.hideOnMobile}`}>
              {truncatedDescription}
            </p>
          )}
          <div className={`${styles.additionalInfo}`}>
            <Duration formattedActivityDuration={formattedActivityDuration} />
            <Flags freeCancellation={freeCancellation} isMobile={false} />
          </div>
        </div>
      </Link>
      <div className={`${styles.buttonWrapper} ${styles.hideOnDesktop}`}>
        <Button
          link={product?.productUrl}
          target="_blank"
          classes={["viator-card-cta"]}
        >
          Book Now!
        </Button>
      </div>
    </article>
  );
}

export function ViatorProductCardMini({
  product,
  formattedLocation,
  formattedActivityDuration,
  truncatedDescription,
  freeCancellation,
  pricingElement,
  imageElement,
  showDescription = true,
  truncateTitleTo = 64,
}) {
  // console.log(product);
  return (
    <article
      className={`${styles.container} ${styles.mini} wn-viator-product-card`}
      id={`viator-product-card-${product?.productCode}`}
    >
      <Link
        href={product.productUrl}
        className={`${styles.link}`}
        target="_blank"
      >
        {imageElement}
        <div className={`${styles.content}`}>
          <div className={`${styles.row1}`}>
            <Title
              title={product.title}
              isMobile={false}
              formattedLocation={formattedLocation}
              truncateTitleTo={truncateTitleTo}
            />
            <Title
              title={product.title}
              isMobile={true}
              formattedLocation={formattedLocation}
            />
          </div>
          <div className={`${styles.row2}`}>
            <StarRating reviews={product.reviews} />
          </div>
          <div className={`${styles.row3}`}>
            <div className={`${styles.duration}`}>
              <Duration formattedActivityDuration={formattedActivityDuration} />
            </div>
            <div className={`${styles.price}`}>{pricingElement}</div>
          </div>
        </div>
      </Link>
    </article>
  );
}

export function ViatorProductCardLarge({
  product,
  formattedLocation,
  formattedActivityDuration,
  truncatedDescription,
  freeCancellation,
  pricingElement,
  imageElement,
  showDescription = true,
  truncateTitleTo = 128,
}) {
  return (
    <article
      className={`${styles.container} ${styles.large} wn-viator-product-card wn-viator-product-card-large`}
      id={`viator-product-card-${product?.productCode}`}
    >
      <Link
        href={product.productUrl}
        className={`${styles.link}`}
        target="_blank"
      >
        <div className={`${styles.imageWrapper}`}>{imageElement}</div>
        <div className={`${styles.content}`}>
          <div className={`${styles.row1}`}>
            <div className={`${styles.titleContainer}`}>
              <div className={`${styles.location}`}>{formattedLocation}</div>
              <div className={`${styles.title}`}>
                <p className={`${styles.title}`}>
                  {truncateText(product?.title, truncateTitleTo)}
                </p>
              </div>
            </div>
          </div>
          <div className={`${styles.row2}`}>
            <StarRating reviews={product.reviews} />
          </div>
          <div className={`${styles.row3}`}>
            <div className={`${styles.durationFlags}`}>
              {formattedActivityDuration && (
                <div className={`${styles.duration}`}>
                  <Duration
                    formattedActivityDuration={formattedActivityDuration}
                    hideOnMobile={false}
                  />
                </div>
              )}
              <Flags
                freeCancellation={freeCancellation}
                isMobile={false}
                hideOnMobile={false}
              />
            </div>
            <div className={`${styles.price}`}>{pricingElement}</div>
          </div>
        </div>
      </Link>
      <div className={`${styles.buttonWrapper} ${styles.hideOnDesktop}`}>
        <Button
          link={product?.productUrl}
          target="_blank"
          classes={["viator-card-cta"]}
        >
          Book Now!
        </Button>
      </div>
    </article>
  );
}

function Title({
  title,
  isMobile = false,
  formattedLocation,
  truncateTitleTo,
}) {
  const titleText = isMobile
    ? truncateText(
        title[0].toUpperCase() + title.slice(1, title.length),
        truncateTitleTo ?? 46
      )
    : truncateText(
        title[0].toUpperCase() + title.slice(1, title.length),
        truncateTitleTo
      );

  return (
    <div
      className={`${styles.titleContainer} ${
        isMobile ? styles.hideOnDesktop : styles.hideOnMobile
      }`}
    >
      {isMobile && (
        <div className={`${styles.location} ${styles.hideOnDesktop}`}>
          {formattedLocation}
        </div>
      )}
      <div
        className={`${styles.title} ${
          isMobile ? styles.hideOnDesktop : styles.hideOnMobile
        }`}
      >
        <p className={`${styles.title}`}>{titleText}</p>
      </div>
    </div>
  );
}

function Duration({ formattedActivityDuration, hideOnMobile = true }) {
  if (!formattedActivityDuration || formattedActivityDuration == null) {
    return null;
  }

  return (
    <div
      className={`${styles.duration} ${styles.iconBox} ${
        hideOnMobile ? styles.hideOnMobile : ""
      }`}
    >
      {formattedActivityDuration && (
        <>
          <span className={`${styles.icon}`}>
            <BsClock size={16} />
          </span>
          <span>{formattedActivityDuration}</span>
          {/* <span>&nbsp;(approx)</span> */}
        </>
      )}
    </div>
  );
}

function Flags({ freeCancellation, isMobile, hideOnMobile = true }) {
  return (
    <div
      className={`${styles.flags} ${
        isMobile && hideOnMobile ? styles.hideOnMobile : ""
      }`}
    >
      {freeCancellation && (
        <span className={`${styles.flag} ${styles.iconBox}`}>
          <span className={`${styles.icon}`}>
            <BsCheck2 size={20} />
          </span>
          Free cancellation
        </span>
      )}
    </div>
  );
}

function StarRating({ reviews, productCode }) {
  const rating = reviews?.combinedAverageRating ?? null;

  if (!rating) {
    return null;
  }
  const stars = generateViatorProductStars(rating, productCode);

  return (
    <>
      <span className={`${styles.stars}`}>
        {[...stars.full, ...stars.halfFull, ...stars.halfEmpty, ...stars.empty]}{" "}
      </span>
      <span className={`${styles.totalReviews}`}>{reviews.totalReviews}</span>
    </>
  );
}

function LoadingSkeleton({ cardType = "full" }) {
  const bodySkeletonRows = {
    large: 12,
    full: 5,
    mini: 2,
  };

  const rowHeights = {
    large: "36px",
    full: "38px",
    mini: "38px",
  };

  return (
    <article
      className={`${styles.container} ${styles.loadingSkeleton} wn-viator-product-card`}
    >
      <>
        <div className={`${styles.imageWrapper}`}>
          <Skeleton
            count={1}
            containerClassName="skeletonContainer"
            height="100%"
            borderRadius="0"
          />
        </div>
        <div className={`${styles.info}`}>
          <Skeleton
            count={bodySkeletonRows[cardType]}
            height={rowHeights[cardType]}
            containerClassName="skeletonContainer"
          />
        </div>
      </>
    </article>
  );
}
